import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import { Form } from 'react-bootstrap';
import { ActionMeta } from 'react-select';
import { useBranchContext } from 'providers/BranchProvider';
import axiosInstance from 'utils/axiosInterceptors';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';
import { AxiosError } from 'axios';
import ReactSelect from 'components/base/ReactSelect';
import { unitOfMeasureMapping, unitOptions } from 'utils/unitOptions';
import { SelectOption } from '../PurchaseOrder/CreatePurchaseOrderForm';
import { useProductTypeahead } from '../PurchaseOrder/useProductTypeahead';

export type EditResupplyItemFormHandle = {
  submit: () => Promise<boolean>;
};

export type EditResupplyItemFormProps = {
  resupplyItemId: number;
  productId: number | null;
  productName: string;
  quantity: number;
  unitOfMeasure: string | number;
  comment: string;
};

const resolveUnitLabel = (unitOfMeasure: string | number) => {
  if (typeof unitOfMeasure === 'number') {
    return unitOptions.find(option => option.value === unitOfMeasure)?.label ?? 'Piece';
  }
  return unitOfMeasureMapping[unitOfMeasure] ? unitOfMeasure : 'Piece';
};

const EditResupplyItemForm = forwardRef<
  EditResupplyItemFormHandle,
  EditResupplyItemFormProps
>(
  (
    {
      resupplyItemId,
      productId,
      productName,
      quantity,
      unitOfMeasure,
      comment
    },
    ref
  ) => {
    const { selectedBranch } = useBranchContext();
    const { options, isLoading, handleSearchChange, noOptionsMessage } =
      useProductTypeahead();
    const [selectedProduct, setSelectedProduct] = useState<SelectOption | null>(
      productId
        ? { value: productId, label: productName || 'Selected product' }
        : null
    );
    const [code, setCode] = useState(productName);
    const [qty, setQty] = useState(quantity);
    const [unit, setUnit] = useState(resolveUnitLabel(unitOfMeasure));
    const [note, setNote] = useState(comment);

    useEffect(() => {
      setSelectedProduct(
        productId
          ? { value: productId, label: productName || 'Selected product' }
          : null
      );
      setCode(productName);
      setQty(quantity);
      setUnit(resolveUnitLabel(unitOfMeasure));
      setNote(comment);
    }, [productId, productName, quantity, unitOfMeasure, comment]);

    const handleProductChange = (
      newValue: unknown,
      actionMeta: ActionMeta<unknown>
    ) => {
      if (actionMeta.action === 'select-option') {
        const option = newValue as SelectOption;
        setSelectedProduct(option);
        setCode('');
        return;
      }
      if (actionMeta.action === 'clear') {
        setSelectedProduct(null);
      }
    };

    const submit = async (): Promise<boolean> => {
      if (!selectedBranch?.branchDomain) {
        toast.error('Branch not selected', { ...toastConfig, autoClose: 3000 });
        return false;
      }
      if (!selectedProduct && !code.trim()) {
        toast.error('Select a product or enter a product name', {
          ...toastConfig,
          autoClose: 3000
        });
        return false;
      }
      if (!qty || qty < 1) {
        toast.error('Quantity must be at least 1', {
          ...toastConfig,
          autoClose: 3000
        });
        return false;
      }

      try {
        await axiosInstance.put(
          `/api/v1/${selectedBranch.branchDomain}/resupply/${resupplyItemId}`,
          selectedProduct
            ? {
                productId: selectedProduct.value,
                quantity: qty,
                unitOfMeasure: unit,
                comment: note.trim() || null
              }
            : {
                productName: code.trim(),
                quantity: qty,
                unitOfMeasure: unit,
                comment: note.trim() || null
              }
        );
        toast.success('Item edited successfully', {
          ...toastConfig,
          autoClose: 2000
        });
        return true;
      } catch (error) {
        if (error instanceof AxiosError) {
          toast.error(
            error.response?.data?.errorMessage ||
              error.response?.data?.message ||
              'Unable to update resupply item',
            { ...toastConfig, autoClose: 3000 }
          );
          return false;
        }
        toast.error('Unable to update resupply item', {
          ...toastConfig,
          autoClose: 3000
        });
        return false;
      }
    };

    useImperativeHandle(ref, () => ({ submit }));

    return (
      <Form>
        <Form.Group className="mb-3">
          <Form.Label>Product</Form.Label>
          <ReactSelect
            value={selectedProduct}
            placeholder="Search catalog (type at least 2 characters)"
            options={options}
            onChange={handleProductChange}
            onInputChange={handleSearchChange}
            filterOption={null}
            noOptionsMessage={noOptionsMessage}
            isLoading={isLoading}
            closeMenuOnSelect
            isSearchable
            isClearable
          />
          <Form.Text className="text-body-tertiary">
            Pick a catalog product, or clear this and enter a name below if it
            is not in the catalog yet.
          </Form.Text>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Product name</Form.Label>
          <Form.Control
            type="text"
            value={code}
            disabled={selectedProduct != null}
            placeholder="OEM / barcode / part code"
            onChange={e => setCode(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Quantity</Form.Label>
          <Form.Control
            type="number"
            min={1}
            value={qty || ''}
            onChange={e => setQty(Number(e.target.value))}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Unit of Measure</Form.Label>
          <Form.Select
            value={unit}
            onChange={e => setUnit(e.target.value)}
          >
            {unitOptions.map(option => (
              <option key={option.label} value={option.label}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-0">
          <Form.Label>Comment (optional)</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            maxLength={500}
            value={note}
            placeholder="e.g. for Toyota Vios"
            onChange={e => setNote(e.target.value)}
          />
        </Form.Group>
      </Form>
    );
  }
);

EditResupplyItemForm.displayName = 'EditResupplyItemForm';

export default EditResupplyItemForm;
